import piano from "../../../../piano";
import player from "../../../../player";
import handleTouch from "../../../../../common/helpers/handleTouch";
import { IBar, INote, IPitch, IScore, IScoreEditorThis } from "../../../../../common/types";

import { getNoteOffset, pianoParser, solfaParser } from "../../../../../common/parameters";
import getKeyOffset from "../../../Task/types/Common/getKeyOffset";
import scrollToBar from "../../../Task/types/Common/scrollToBar";
import { parseDo } from "../changeDo";
import { getAccidentals } from "../../score-display/utils";

interface IGetPianoToneProps {
  s: IPitch["s"],
  a: IPitch["a"],
  o: IPitch["o"],
  score: IScore,
  activeVoice: number,
  doPosParsed: {
    value: number,
    alt: number,
  }
};

const getPianoTone = ({ s, a, o = 0, score, activeVoice, doPosParsed }: IGetPianoToneProps) => {
  return (
      60 +
      pianoParser[s] +
      (a || 0) +
      getKeyOffset(doPosParsed.value + 6) +
      doPosParsed.alt +
      (o + (score["octave" + activeVoice] || 0) || 0) * 12
  );
};

// TODO nextBar
const getNextNote = (bars: IBar[], bar: number, note: number) => {
    if (bars[bar].notes.length - 1 > note) {
        return bars[bar].notes[note + 1];
    } else {
        return bars[bar + 1].notes[0];
    }
};

export async function handleSolfaInput(this: IScoreEditorThis, p: IPitch | number | null, direction: 1 | -1 | null, isPiano: boolean, e: KeyboardEvent | MouseEvent | TouchEvent) {
  const intervalsTask = this.props.taskType === "intervals";
  const chords3Task = this.props.taskType === "chords3";
  const isAddInterval = (e!.ctrlKey || e!.metaKey) && (intervalsTask || chords3Task);
  if (isPiano) {
      if (!(await handleTouch(e))) return;
  }
  const {
      score: _score,
      activeVoice,
  } = this.state;
  const score = { ..._score };
  const accidentals = getAccidentals(score);
  const bars = score["bars" + (activeVoice || "")] as IBar[];
  const { selectedNote } = this.state;
  let currentNote: INote = { l: 4 };
  let writingNote: {
      bar: number,
      note: number,
  };
  if (selectedNote && bars[selectedNote.bar]) {
      currentNote = bars[selectedNote.bar]?.notes[selectedNote.note];
      writingNote = selectedNote;
      if (p && !isAddInterval) {
          let isLastNote = bars[selectedNote.bar].notes.length - 1 === selectedNote.note;
          if (!currentNote?.r) {
              let nextBar: number | null = selectedNote.bar + (isLastNote ? 1 : 0);
              let nextNote: number | null = isLastNote ? 0 : selectedNote.note + 1;
              while (bars[nextBar!]?.notes?.[nextNote!]?.r) {
                  if (nextNote !== bars[nextBar!].notes.length - 1) {
                      nextNote!++;
                  } else {
                      nextBar!++;
                      nextNote = 0;
                      if (!bars[nextBar!]) {
                          nextBar = null;
                          nextNote = null;
                      }
                  }
              }
              if (!bars[nextBar!]?.notes[nextNote!]) {
                  this.deselectNote();
              } else {
                  this.setState({
                      selectedNote: {
                          bar: nextBar,
                          note: nextNote,
                      },
                  });
              }
          }
          if (!currentNote) return;
      }
      setTimeout(() => {
          scrollToBar(this.distribution, this.scoreRef, selectedNote.bar);
      }, 500);
  } else if (p) {
      let find = false;
      let bar: number | undefined;
      let note: number | undefined;
      let lastCheckedBar: number | undefined;
      let lastCheckedNote: number | undefined;
      outer: for (let b = 0; b < bars.length; b++) {
          if (bars[b] === null) continue;
          if (bars[b].notes.length === 0) {
              if (lastCheckedNote && isAddInterval) {
                  find = true;
              }
              break;
          }
          bar = b;
          for (let n = 0; n < bars[b].notes.length; n++) {
              note = n;
              if (bars[b].notes[n].p === undefined && !bars[b].notes[n].r) {
                  find = true;
                  break outer;
              }
              if (!bars[b].notes[n].r) {
                  lastCheckedNote = note;
                  lastCheckedBar = bar;
              }
          }
      }
      if (isAddInterval) {
          bar = lastCheckedBar;
          note = lastCheckedNote;
      }
      if ((bar === undefined || !find) && !(note !== undefined && isAddInterval)) return;
      currentNote = bars[bar!].notes[note!];
      writingNote = {
          bar: bar!,
          note: note!,
      };
      setTimeout(() => {
          scrollToBar(this.distribution, this.scoreRef, bar!);
      }, 500);
  }
  const doPosParsed = parseDo(accidentals[writingNote.bar]);

  if (typeof p === "number") {
    const keyOffset = getKeyOffset(doPosParsed.value + 6) + doPosParsed.alt;
    let noteOffset = p - (keyOffset + 60);
    // Octave
    let o = Math.floor(noteOffset / 12) as IPitch["o"];
    if (isPiano) {
        o! -= score["octave" + (activeVoice || "0")] || 0;
    }
    while (noteOffset < 0) {
        noteOffset += 12;
    }
    let { s, a } = getNoteOffset(noteOffset % 12);
    p = { o, s, a };
}
  if (currentNote.r) return;
  let s: IPitch["s"], a: IPitch["a"], o: IPitch["o"];
  if (!p) {
      o = (currentNote.p as IPitch).o || 0;
      if (direction === 1) {
          if ((currentNote.p as IPitch).s === 6) o = (((currentNote.p as IPitch).o || 0) + 1) as IPitch["o"];
      } else {
          if ((currentNote.p as IPitch).s === 1) o = (((currentNote.p as IPitch).o || 0) - 1) as IPitch["o"];
      }
      let solfaIndex = solfaParser.indexOf((currentNote.p as IPitch).s);
      solfaIndex += direction!;
      solfaIndex = (solfaIndex + 7) % 7;
      s = solfaParser[solfaIndex];
      a = 0;
  } else {
      ({ s, a, o } = p);
  }
  if (isAddInterval && currentNote.l === 1) {
      let canAdd = true;
      if (Array.isArray(currentNote.p)) {
          const pitchIndex = currentNote.p.findIndex(pitch => pitch.s === s && (pitch.o || 0) === o);
          if (pitchIndex > -1) {
              currentNote.p.splice(pitchIndex, 1);
              if (currentNote.p.length === 1) {
                  currentNote.p = currentNote.p[0];
              }
              canAdd = false;
          }
      } else {
          const pitch = currentNote.p;
          if (!pitch) {
              currentNote.p = { s };
              if (a !== 0) currentNote.p.a = a;
              if (o !== 0) currentNote.p.o = o;
              canAdd = false;
          } else if (pitch.s === s && (pitch.o || 0) === o && !intervalsTask) {
              currentNote.p = [];
          }
      }

      if (canAdd) {
          if (Array.isArray(currentNote.p)) {
              if (intervalsTask && currentNote.p.length === 2) {
                  return;
              }
              if (chords3Task && currentNote.p.length === 4) {
                  return;
              }
              currentNote.p.push({ s });
          } else {
              currentNote.p = [currentNote.p!, { s }];
          }
          const nextP = currentNote.p[currentNote.p.length - 1];
          if (a !== 0) nextP.a = a;
          if (o !== 0) nextP.o = o;
          currentNote.p = currentNote.p
              .sort((noteA, noteB) => {
                  return (
                      getPianoTone({ s: noteB.s, a: noteB.a, o: noteB.o, score, activeVoice, doPosParsed }) -
                      getPianoTone({ s: noteA.s, a: noteA.a, o: noteA.o, score, activeVoice, doPosParsed })
                  );
              })
              .reduce((all: IPitch[], next) => {
                  if (
                      !intervalsTask &&
                      all.find(note => note.s === next.s && note.a === next.a && note.o === next.o)
                  ) {
                      return all;
                  }
                  return [...all, next];
              }, []);
          if (currentNote.p.length === 1) {
              currentNote.p = currentNote.p[0];
          }
      }
  } else {
      currentNote.p = { s };
      if (a !== 0) currentNote.p.a = a;
      if (o !== 0) currentNote.p.o = o;
  }
  let pianoTone = getPianoTone({ s, a, o, score, activeVoice, doPosParsed });
  if (player.context) {
      piano.play(pianoTone, player.context.currentTime, 0.6);
  }
  let lastNote;
  // TODO in circle
  if (currentNote.tie) {
      lastNote = currentNote;
      currentNote = getNextNote(bars, writingNote!.bar, writingNote!.note);
      currentNote.p = lastNote.p;
  }
  this.setState({ score });
}
